import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminSkeletonComponent,
  AdminToastService,
  StatusBadgeComponent,
} from '../../../shared/admin';
import {
  SCHEDULER_STATUS_LABELS,
  schedulerStatusBadgeVariant,
  type ReminderCronHealth,
} from '../../../shared/models/reminder-cron.models';
import { ReminderCronService } from '../../../shared/services/reminder-cron.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-settings-reminder-cron-section',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    AdminButtonComponent,
    AdminCardComponent,
    AdminSkeletonComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './settings-reminder-cron-section.component.html',
  styleUrl: './settings-reminder-cron-section.component.scss',
})
export class SettingsReminderCronSectionComponent {
  private readonly reminderCronService = inject(ReminderCronService);
  private readonly authService = inject(AuthService);
  private readonly permissions = inject(PermissionService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);

  readonly health = signal<ReminderCronHealth | null>(null);
  readonly loading = signal(true);
  readonly refreshing = signal(false);
  readonly savingEnabled = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly saveError = signal<string | null>(null);

  readonly showManage = computed(() => this.permissions.canShow('settings.general', 'manageCron'));
  readonly canManage = computed(() => this.permissions.canExecute('settings.general', 'manageCron'));

  readonly schedulerStatusLabel = computed(() => {
    const status = this.health()?.schedulerStatus;
    return status ? SCHEDULER_STATUS_LABELS[status] : '—';
  });

  readonly statusVariant = computed(() => {
    const status = this.health()?.schedulerStatus;
    return status ? schedulerStatusBadgeVariant(status) : 'draft';
  });

  readonly cronEnabledLabel = computed(() => (this.health()?.cronEnabled ? 'Yes' : 'No'));

  readonly showHealthyNoRemindersNote = computed(() => {
    const data = this.health();
    return Boolean(
      data?.cronEnabled &&
        data.schedulerStatus === 'running' &&
        data.pendingReminderCount === 0 &&
        !data.nextReminderDueAt,
    );
  });

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(null);

    this.reminderCronService.getHealth().subscribe({
      next: (data) => {
        this.health.set(data);
        this.loading.set(false);
        this.refreshing.set(false);
      },
      error: () => {
        this.loadError.set('Unable to load reminder cron status. Please try again.');
        this.loading.set(false);
        this.refreshing.set(false);
      },
    });
  }

  refresh(): void {
    this.refreshing.set(true);
    this.loadError.set(null);

    this.reminderCronService.getHealth().subscribe({
      next: (data) => {
        this.health.set(data);
        this.refreshing.set(false);
      },
      error: () => {
        this.loadError.set('Unable to refresh reminder cron status. Please try again.');
        this.refreshing.set(false);
      },
    });
  }

  async onEnabledToggle(nextEnabled: boolean): Promise<void> {
    if (!this.canManage() || this.savingEnabled()) {
      return;
    }

    const current = this.health();
    if (!current || current.cronEnabled === nextEnabled) {
      return;
    }

    if (!nextEnabled) {
      const confirmed = await this.confirmDialog.confirm({
        title: 'Disable task reminder cron?',
        message:
          'Task reminder emails and notifications will be paused immediately. Reminder data will be kept intact.',
        confirmLabel: 'Disable cron',
        confirmVariant: 'danger',
      });
      if (!confirmed) {
        return;
      }
    }

    this.savingEnabled.set(true);
    this.saveError.set(null);

    this.reminderCronService.setEnabled(nextEnabled).subscribe({
      next: (data) => {
        this.health.set(data);
        this.savingEnabled.set(false);
        this.toast.success(nextEnabled ? 'Task reminder cron enabled' : 'Task reminder cron disabled');
      },
      error: () => {
        this.saveError.set('Unable to update reminder cron setting. Please try again.');
        this.savingEnabled.set(false);
      },
    });
  }
}
